import { ReactLink } from '@dfl/react-security';
import { DirectionsCarOutlined, ErrorOutline, HandshakeOutlined } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import { IOrder } from '../../interfaces/IOrder';
import { IconButton } from '@dfl/mui-react-common';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useDispatchDetail } from 'modules/sales/dispatch/contexts/dispatchContext';
import { DispatchIcon } from '../icons/DispatchIcon';
import { usePaymentAgreementDetail } from 'modules/conciliation/payment-agreement/contexts/paymentAgreementContext';
import { DISPATCH_ROUTE } from 'modules/sales/dispatch/constants/dispatch-route';
import { PAYMENT_AGREEMENT_ROUTE } from 'modules/conciliation/payment-agreement/constants/payment-agreement-route';

type OrderCodeCellProps = {
  link: string;
  value: string;
  isSubOrder?: boolean;
  record: IOrder;
};

const OrderCodeCell = ({ link, value, isSubOrder, record }: OrderCodeCellProps) => {
  const navigate = useNavigate();
  const { dispatchId } = useDispatchDetail();
  const { paymentAgreementId } = usePaymentAgreementDetail();
  const { t } = useTranslation(['dispatch', 'paymentAgreement']);
  const isDispatch = useMemo(() => [record?.dispatch, isSubOrder].every(Boolean), [record?.dispatch, isSubOrder]);
  const isPaymentAgreement = useMemo(
    () => [record?.paymentAgreement, isSubOrder].every(Boolean),
    [record?.paymentAgreement, isSubOrder],
  );

  const onDispatch = useCallback(() => {
    navigate(`${DISPATCH_ROUTE.DETAIL(record?.dispatch as string)}/orders`);
  }, [navigate, record?.dispatch]);

  const onPaymentAgreement = useCallback(() => {
    navigate(PAYMENT_AGREEMENT_ROUTE.DETAIL(record?.paymentAgreement as string));
  }, [navigate, record?.paymentAgreement]);

  const onCarrier = useCallback(() => {
    navigate(`/inventory/provider/carriers/${(record?.carrier?.carrier?._id as string)}/general`);
  }, [navigate, record?.carrier?.carrier?._id]);

  return (
    <Stack maxWidth={300} alignItems={'center'} flexDirection={'row'} gap={0.5}>
      <ReactLink to={link} underline={'hover'}>
        <Typography noWrap lineHeight={1} title={value}>
          {value}
        </Typography>
      </ReactLink>

      {/* suborder by dispatch */}
      {isDispatch && !dispatchId && (
        <IconButton color='primary' onClick={onDispatch} tooltip={t('dispatch:goDispatch')}>
          <DispatchIcon />
        </IconButton>
      )}

      {/* suborder by paymentAgreement */}
      {isPaymentAgreement && !paymentAgreementId && (
        <IconButton color='primary' onClick={onPaymentAgreement} tooltip={t('paymentAgreement:goPaymentAgreement')}>
          <HandshakeOutlined />
        </IconButton>
      )}

      {!!record?.carrier?.carrier && (
        <IconButton sx={{ cursor: 'pointer' }} tooltip={t('provider:carrierDetail')} onClick={onCarrier}>
          <DirectionsCarOutlined color='primary' />
        </IconButton>
      )}

      {record?.metrics?.totalIncidences > 0 && (
        <IconButton sx={{ cursor: 'pointer' }} tooltip={t('incidence:hasIncidences', { value: record?.metrics?.totalIncidences })}>
          <ErrorOutline color='error' fontSize='small' />
        </IconButton>
      )}
    </Stack>
  );
};

export default memo(OrderCodeCell);
